import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Play, TrendingUp, Sparkles } from "lucide-react";
import { LINKS } from "@/lib/links";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] bg-rim-night text-rim-text overflow-hidden pt-20 md:pt-28 pb-20 md:pb-28 flex items-center"
    >
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(124,58,237,0.35), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(168,85,247,0.25), transparent 60%), radial-gradient(ellipse 60% 50% at 15% 70%, rgba(76,29,149,0.3), transparent 60%), #0d0a1f",
          }}
        />
        {/* Slow mesh animation */}
        <motion.div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 20% 30%, rgba(124,58,237,0.45), transparent 55%), radial-gradient(ellipse 40% 35% at 80% 60%, rgba(168,85,247,0.35), transparent 55%)",
          }}
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Particles */}
        {[
          { x: "12%", y: "22%", d: 0 },
          { x: "82%", y: "18%", d: 1.4 },
          { x: "70%", y: "62%", d: 0.8 },
          { x: "22%", y: "75%", d: 2.1 },
          { x: "55%", y: "45%", d: 1.1 },
          { x: "90%", y: "85%", d: 0.4 },
        ].map((p, i) => (
          <motion.span
            key={i}
            className="absolute h-1.5 w-1.5 rounded-full bg-rim-glow/40 blur-[1px]"
            style={{ left: p.x, top: p.y }}
            animate={{ y: [0, -16, 0], opacity: [0.3, 0.7, 0.3] }}
            transition={{
              duration: 5 + p.d,
              repeat: Infinity,
              ease: "easeInOut",
              delay: p.d,
            }}
          />
        ))}
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse at center, black 35%, transparent 80%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full">
        <div className="relative max-w-3xl">
          {/* Badge */}
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 bg-rim/20 border border-rim/60 rounded-full px-3 py-1.5 text-[12px] font-semibold text-rim-glow"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Metodo RIM™
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="font-heading mt-5 text-[40px] sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tighter text-balance"
            data-testid="hero-title"
          >
            Alla comunicazione{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rim-glow via-fuchsia-400 to-indigo-300">
              ci pensiamo noi.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-[15px] md:text-lg text-rim-muted leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            Contenuti, video e strategia per la tua attività.
            <br />
            Tu gestisci il lavoro — noi costruiamo la tua presenza digitale.
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.32 }}
            className="mt-8 w-full sm:max-w-sm"
          >
            <a
              href={LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-call-btn"
              className="group relative w-full inline-flex items-center justify-center gap-2 bg-rim hover:bg-rim-hover text-white font-bold text-base h-[52px] rounded-2xl transition-all duration-300 shadow-[0_10px_40px_-8px_rgba(124,58,237,0.7)] hover:shadow-[0_15px_50px_-8px_rgba(168,85,247,0.9)] animate-pulse-glow"
            >
              <MessageCircle className="h-5 w-5" />
              Contattaci
            </a>
            <p className="mt-3 text-center text-[12px] text-rim-muted">
              Siamo con te in ogni step
            </p>
          </motion.div>
        </div>

        {/* Floating social-proof cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="hidden md:block absolute top-12 right-6 lg:right-12"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{ rotate: 5 }}
            className="bg-rim-surface/95 backdrop-blur-md border border-rim/40 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_20px_50px_-15px_rgba(124,58,237,0.5)]"
          >
            <span className="h-8 w-8 rounded-lg bg-rim/30 grid place-items-center">
              <Play className="h-4 w-4 text-rim-glow fill-rim-glow" />
            </span>
            <div className="leading-tight">
              <p className="font-heading font-bold text-white text-sm">
                826K views
              </p>
              <p className="text-[10px] text-rim-glow font-medium">
                @sinergy.tech
              </p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="hidden md:block absolute bottom-16 left-[55%]"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            style={{ rotate: -3 }}
            className="bg-rim-surface/95 backdrop-blur-md border border-rim/40 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_20px_50px_-15px_rgba(124,58,237,0.5)]"
          >
            <span className="h-8 w-8 rounded-lg bg-emerald-500/20 grid place-items-center">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
            </span>
            <div className="leading-tight">
              <span className="inline-block bg-emerald-500/20 text-emerald-300 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider mb-0.5">
                Reel
              </span>
              <p className="font-heading font-bold text-white text-sm">
                +828% reach
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
